import merge from 'deepmerge';

import ehiTokens from './src/tokens/_variables.js';
import typography from './src/tokens/_typography.js';

import color from './src/tokens/_color.js';
import opacity from './src/tokens/_opacity.js';
import spacing from './src/tokens/_space.js';
import animations from './src/tokens/_animations.js';
import vueTransitions from './src/tokens/_vue-transitions.js';
import reset from './src/tokens/_reset.js';

const { animation, keyframes } = animations;

export const ehiTailwindTheme = {
  screens: {
    xs: '320px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
    print: { raw: 'print' },
  },
  container: {
    center: true,
  },
  textColor: color.text,
  backgroundColor: color.background,
  accentColor: color.accentColor,
  borderColor: color.border,
  outlineColor: color.border,
  ringColor: color.border,
  ringOffsetColor: color.border,
  divideColor: color.border,
  caretColor: color.border,
  textDecorationColor: color.border,
  opacity,
  animation,
  keyframes,
  textDecorationThickness: {
    2: '2px',
    4: '4px',
  },
  borderSpacing: ({ theme }) => ({
    ...theme('spacing'),
    ...spacing,
  }),
  flexBasis: ({ theme }) => ({
    auto: 'auto',
    ...theme('spacing'),
    '1/2': '50%',
    '1/3': '33.333333%',
    '2/3': '66.666667%',
    '1/4': '25%',
    '2/4': '50%',
    '3/4': '75%',
    '1/5': '20%',
    '2/5': '40%',
    '3/5': '60%',
    '4/5': '80%',
    '1/6': '16.666667%',
    '2/6': '33.333333%',
    '3/6': '50%',
    '4/6': '66.666667%',
    '5/6': '83.333333%',
    '1/12': '8.333333%',
    '2/12': '16.666667%',
    '3/12': '25%',
    '4/12': '33.333333%',
    '5/12': '41.666667%',
    '6/12': '50%',
    '7/12': '58.333333%',
    '8/12': '66.666667%',
    '9/12': '75%',
    '10/12': '83.333333%',
    '11/12': '91.666667%',
    full: '100%',
    ...spacing,
  }),
  gap: ({ theme }) => ({ ...theme('spacing'), ...spacing }),
  height: ({ theme }) => ({
    auto: 'auto',
    ...theme('spacing'),
    ...spacing,
    '1/2': '50%',
    '1/3': '33.333333%',
    '2/3': '66.666667%',
    '1/4': '25%',
    '2/4': '50%',
    '3/4': '75%',
    '1/5': '20%',
    '2/5': '40%',
    '3/5': '60%',
    '4/5': '80%',
    '1/6': '16.666667%',
    '2/6': '33.333333%',
    '3/6': '50%',
    '4/6': '66.666667%',
    '5/6': '83.333333%',
    full: '100%',
    screen: '100vh',
    svh: '100svh',
    lvh: '100lvh',
    dvh: '100dvh',
    min: 'min-content',
    max: 'max-content',
    fit: 'fit-content',
  }),
  inset: ({ theme }) => ({
    auto: 'auto',
    ...theme('spacing'),
    ...spacing,
    '1/2': '50%',
    '1/3': '33.333333%',
    '2/3': '66.666667%',
    '1/4': '25%',
    '2/4': '50%',
    '3/4': '75%',
    full: '100%',
  }),
  margin: ({ theme }) => ({
    auto: 'auto',
    ...theme('spacing'),
    ...spacing,
  }),
  maxHeight: ({ theme }) => ({
    ...theme('spacing'),
    none: 'none',
    full: '100%',
    screen: '100vh',
    svh: '100svh',
    lvh: '100lvh',
    dvh: '100dvh',
    min: 'min-content',
    max: 'max-content',
    fit: 'fit-content',
  }),
  padding: ({ theme }) => ({
    ...theme('spacing'),
    ...spacing,
  }),
  scrollMargin: ({ theme }) => ({
    ...theme('spacing'),
    ...spacing,
  }),
  scrollPadding: ({ theme }) => ({
    ...theme('spacing'),
    ...spacing,
  }),
  space: ({ theme }) => ({
    ...theme('spacing'),
    ...spacing,
  }),
  spacing: {
    px: '1px',
    0: '0px',
    0.5: '0.125rem',
    1: '0.25rem',
    1.5: '0.375rem',
    2: '0.5rem',
    2.5: '0.625rem',
    3: '0.75rem',
    3.5: '0.875rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    7: '1.75rem',
    8: '2rem',
    9: '2.25rem',
    10: '2.5rem',
    11: '2.75rem',
    12: '3rem',
    14: '3.5rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
    28: '7rem',
    32: '8rem',
    36: '9rem',
    40: '10rem',
    44: '11rem',
    48: '12rem',
    52: '13rem',
    56: '14rem',
    60: '15rem',
    64: '16rem',
    72: '18rem',
    80: '20rem',
    96: '24rem',
    ...spacing,
  },
  textIndent: ({ theme }) => ({
    ...theme('spacing'),
    ...spacing,
  }),
  translate: ({ theme }) => ({
    ...theme('spacing'),
    ...spacing,
    '1/2': '50%',
    '1/3': '33.333333%',
    '2/3': '66.666667%',
    '1/4': '25%',
    '2/4': '50%',
    '3/4': '75%',
    full: '100%',
  }),
  width: ({ theme }) => ({
    auto: 'auto',
    ...theme('spacing'),
    ...spacing,
    '1/2': '50%',
    '1/3': '33.333333%',
    '2/3': '66.666667%',
    '1/4': '25%',
    '2/4': '50%',
    '3/4': '75%',
    '1/5': '20%',
    '2/5': '40%',
    '3/5': '60%',
    '4/5': '80%',
    '1/6': '16.666667%',
    '2/6': '33.333333%',
    '3/6': '50%',
    '4/6': '66.666667%',
    '5/6': '83.333333%',
    '1/12': '8.333333%',
    '2/12': '16.666667%',
    '3/12': '25%',
    '4/12': '33.333333%',
    '5/12': '41.666667%',
    '6/12': '50%',
    '7/12': '58.333333%',
    '8/12': '66.666667%',
    '9/12': '75%',
    '10/12': '83.333333%',
    '11/12': '91.666667%',
    full: '100%',
    screen: '100vw',
    svw: '100svw',
    lvw: '100lvw',
    dvw: '100dvw',
    min: 'min-content',
    max: 'max-content',
    fit: 'fit-content',
  }),
};

export const setGlobalTheme = (existingTailwindTheme) => {
  const overwriteMerge = (destinationArray, sourceArray) => sourceArray;

  return merge(existingTailwindTheme, ehiTailwindTheme, {
    arrayMerge: overwriteMerge,
  });
};

export const getGlobalTokenCssString = () => {
  return `
              ${ehiTokens}
                ${typography}
                ${vueTransitions}
                ${reset}
            `;
};

export const setGlobalTokens = () => {
  if (typeof window !== 'undefined') {
    if (!document.getElementById('ehi-tokens')) {
      const css = getGlobalTokenCssString();
      const head = document.head || document.getElementsByTagName('head')[0],
        style = document.createElement('style');
      style.type = 'text/css';
      style.setAttribute('id', 'ehi-tokens');
      style.appendChild(document.createTextNode(css));
      head.appendChild(style);
    }
  }
};
